import { TooltipProvider } from "@capacit-com/lara-law-shared-ui";
import { DocumentSearchControls } from "@/components/document/DocumentSearchControls";
import { DocumentToolbarGroup } from "@/partials/document-renderer/components/document-toolbar/DocumentToolbarGroup";
import { DocumentViewGroup } from "@/partials/document-renderer/components/document-toolbar/DocumentViewGroup";
import { DocumentFollowGroup } from "@/partials/document-renderer/components/document-toolbar/DocumentFollowGroup";
import { DocumentExportGroup } from "@/partials/document-renderer/components/document-toolbar/DocumentExportGroup";
import { toolbarTier } from "@/partials/document-renderer/components/document-toolbar/documentToolbarTiers";
import WorkspaceDocumentVersionIndicator from "./WorkspaceDocumentVersionIndicator";
import { useFavoriteToggle } from "@/hooks/useFavoriteToggle";
import { useSubscribeAction } from "@/hooks/useSubscribeAction";
import { DocumentCategory } from "@/models/legislation/Legislation";
import { SubscriptionCategory } from "@/models/subscriptions/Subscription";

interface DocumentSearchProps {
  query: string;
  setQuery: (query: string) => void;
  currentIndex: number;
  totalMatches: number;
  goToNext: () => void;
  goToPrevious: () => void;
  clearSearch: () => void;
}

export type WorkspaceFavoriteSubscribeProps = {
  documentId: string;
  workspaceId: string;
  title: string;
  isFavorite: boolean;
  isSubscribed: boolean;
  isReady: boolean;
};

interface WorkspaceDocumentHeaderProps {
  onPrint: () => void;
  searchProps?: DocumentSearchProps;
  zoomLevel?: number;
  onZoomChange?: (level: number) => void;
  onFitToWidth?: () => void;
  favoriteSubscribe?: WorkspaceFavoriteSubscribeProps;
}

/**
 * The workspace viewer's toolbar: the same groups as the legislation viewer,
 * minus the ones a workspace document has nothing to put in them (there is no
 * consolidation to compare and no original PDF to download).
 */
const WorkspaceDocumentHeader: React.FC<WorkspaceDocumentHeaderProps> = ({
  onPrint,
  searchProps,
  zoomLevel,
  onZoomChange,
  onFitToWidth,
  favoriteSubscribe,
}) => {
  const entityPath = favoriteSubscribe
    ? `workspace/${favoriteSubscribe.workspaceId}/document/${favoriteSubscribe.documentId}`
    : "";

  const favorite = useFavoriteToggle({
    documentCategory: DocumentCategory.Workspace,
    documentId: favoriteSubscribe?.documentId ?? "",
    title: favoriteSubscribe?.title ?? "",
    isFavorite: favoriteSubscribe?.isFavorite ?? false,
    workspaceId: favoriteSubscribe?.workspaceId,
  });

  const subscribe = useSubscribeAction({
    subscribeCategory: SubscriptionCategory.WorkspaceDocument,
    entityPath,
    entityName: favoriteSubscribe?.title ?? "",
    source: "workspace",
    isSubscribed: favoriteSubscribe?.isSubscribed ?? false,
  });

  return (
    <TooltipProvider>
      <DocumentToolbarGroup
        label="Find og gå til"
        segmented={false}
        className="shrink-0"
      >
        {searchProps && (
          <DocumentSearchControls
            searchProps={searchProps}
            inputId="workspace-doc-search-input"
            triggerId="workspace-doc-search-trigger"
            previousButtonId="workspace-doc-search-previous-btn"
            nextButtonId="workspace-doc-search-next-btn"
          />
        )}
      </DocumentToolbarGroup>

      <span className={toolbarTier.spacer} aria-hidden="true" />

      <DocumentToolbarGroup label="Dokument" className="w-auto min-w-0 shrink">
        <WorkspaceDocumentVersionIndicator />
      </DocumentToolbarGroup>

      <span className={toolbarTier.spacer} aria-hidden="true" />

      {zoomLevel !== undefined && onZoomChange && (
        <DocumentViewGroup
          zoomLevel={zoomLevel}
          onZoomChange={onZoomChange}
          onFitToWidth={onFitToWidth}
          idPrefix="workspace-doc"
        />
      )}

      {favoriteSubscribe && (
        <DocumentFollowGroup
          isReady={favoriteSubscribe.isReady}
          isFavorite={favoriteSubscribe.isFavorite}
          isSubscribed={favoriteSubscribe.isSubscribed}
          favoriteLabel={favorite.label}
          subscribeLabel={subscribe.label}
          favoriteLoading={favorite.isLoading}
          subscribeLoading={subscribe.isLoading}
          onFavorite={() => {
            void favorite.toggle();
          }}
          onSubscribe={() => {
            void subscribe.activate();
          }}
          idPrefix="workspace-doc"
        />
      )}

      <DocumentExportGroup onPrint={onPrint} idPrefix="workspace-doc" />
    </TooltipProvider>
  );
};

export default WorkspaceDocumentHeader;
